/**
 * TODO: if ad has an internal link, we should do a NextLink navigation instead.
 */
import dynamic from 'next/dynamic';
import PropTypes from 'prop-types';
import { memo } from 'react';

import Box from 'shopper/components/Box';
import Button from 'shopper/components/Button';
import Icon from 'shopper/components/Icon';

import Anchor from 'components/Anchor';
import Image from 'components/Image';

import useClientSide from 'hooks/useClientSide';
import useDialog from 'hooks/useDialog';
import useLocalStorageState from 'hooks/useLocalStorageState';
import useMediaQuery from 'hooks/useMediaQuery';

import { sendEvent } from 'lib/gtag';
import { toImageWithPreservedAspectRatioSize } from 'lib/image';

import { useUser } from 'providers/UserProvider';

import APP from 'constants/app';

const LoginDialog = dynamic(() => import('components/LoginDialog'), {
  ssr: false,
});

const OfferGridCardWithAd = ({
  description,
  gaEventAction = 'card_banner__click',
  gaEventCategory,
  id,
  image = null,
  isClosable = false,
  mobileImage = null,
  url,
  visibility,
  onClick,
}) => {
  const isClientSide = useClientSide();
  const { isMd } = useMediaQuery();
  const { isLoggedIn, loadingLoggedInUser } = useUser();
  const { showDialog } = useDialog();
  const [isEnabledByLocalStorage, setEnabledByLocalStorage] =
    useLocalStorageState(`promobit.timeline.is-card-ad-${id}-enabled`, true);

  const onAdClick = (event) => {
    /**
     * Due to SSoT and the impossibility to use hooks inside objects, we take
     * advantage that all injected cardAds have `onClick` to know that when the
     * ad is clicked, we need to open `LoginDialog`.
     */
    if (onClick && !isLoggedIn && !loadingLoggedInUser) {
      event.preventDefault();
      showDialog(LoginDialog);
    }

    sendEvent({
      action: gaEventAction,
      category: gaEventCategory,
      label: visibility,
    });
  };

  const onCloseClick = () => {
    setEnabledByLocalStorage(false);
  };

  const isAdFromRemoteImagePath = !image.includes(APP.LOCAL_IMAGE_PATH);
  const isDisabled =
    (!mobileImage && !image) ||
    (isClientSide
      ? !isEnabledByLocalStorage || (isMd && !image) || (!isMd && !mobileImage)
      : false);

  if (isDisabled) {
    return null;
  }

  return (
    <Box className="relative block cursor-pointer overflow-hidden rounded-2 md:flex md:justify-between">
      <Anchor
        className="flex h-full items-center justify-center"
        href={url}
        rel="noreferrer"
        target={id === 10022025 ? '_self' : '_blank'}
        onClick={onAdClick}
      >
        <Image
          alt={description}
          className="flex h-auto max-h-[199px] w-auto rounded-4 object-contain md:hidden"
          height={199}
          sizes="(max-width: 768px) 328px, 1px"
          src={
            isAdFromRemoteImagePath
              ? toImageWithPreservedAspectRatioSize(mobileImage, 328)
              : mobileImage
          }
          srcSet={
            isAdFromRemoteImagePath
              ? `
                ${APP.LOCAL_IMAGE_PATH}/general/placeholder.jpg 1w,
                ${toImageWithPreservedAspectRatioSize(mobileImage, 492)} 492w,
                ${toImageWithPreservedAspectRatioSize(mobileImage, 656)} 656w
              `
              : null
          }
          width={328}
        />
        <Image
          alt={description}
          className="hidden size-full object-contain lg:flex lg:h-auto lg:max-h-[407px] lg:w-auto lg:rounded-4"
          height={407}
          sizes="(max-width: 768px) 1px, 224px"
          src={
            isAdFromRemoteImagePath
              ? toImageWithPreservedAspectRatioSize(image, 224)
              : image
          }
          srcSet={
            isAdFromRemoteImagePath
              ? `
                ${APP.LOCAL_IMAGE_PATH}/general/placeholder.jpg 1w,
                ${toImageWithPreservedAspectRatioSize(image, 336)} 336w,
                ${toImageWithPreservedAspectRatioSize(image, 448)} 448w
              `
              : null
          }
          width={224}
        />
      </Anchor>
      {isClosable && (
        <Button
          className="absolute right-0 top-0 mr-2 mt-2"
          icon={<Icon name="close" />}
          size="size4"
          type="secondary-stroke"
          onClick={onCloseClick}
        />
      )}
    </Box>
  );
};

OfferGridCardWithAd.propTypes = {
  description: PropTypes.string.isRequired,
  // Used only for hardcoded card ad and are relevant for SEO
  gaEventAction: PropTypes.string,
  gaEventCategory: PropTypes.string.isRequired,
  id: PropTypes.number.isRequired,
  image: PropTypes.string,
  isClosable: PropTypes.bool,
  mobileImage: PropTypes.string,
  url: PropTypes.string,
  visibility: PropTypes.string.isRequired,
  // Used only for hardcoded card ad
  onClick: PropTypes.func,
};

export default memo(OfferGridCardWithAd);
